import React from "react";
import { useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  startOrder,
  startEditOrder,
  cancelOrder,
  answerComboQuestion,
} from "../ducks/shoppingCartSlice";
import { requireProducts } from "../ducks/productsSlice";
import { STATE_STEP } from "../ducks/consts";

/**
 * Abre um item na sacola ao entrar na tela de produto (novo ou edição de um
 * existente), carrega as perguntas e marca as de combo. Vale para o celular e
 * para o totem.
 *
 * Aberto pelo "Peça também" da sacola (location.state.upsell), o item novo sai
 * marcado com `upsell`. Na edição a marca vem do item editado.
 */
export default function useProductOrderSession({ productId, orderUuidEdit }) {
  const dispatch = useDispatch();
  const stateStep = useSelector(
    (state) => state.products.questionsStateByProduct[productId],
  );
  const { isAvailableToOrder, kilogramStep } = useSelector(
    (state) => state.shop,
  );

  const fromUpsell = useLocation().state?.upsell === true;
  const [orderUuid, setOrderUuid] = React.useState(null);
  // Para onde voltar: a sacola, se é a edição de um item; senão a home.
  const returnPath = orderUuidEdit ? "/checkout" : "/";
  const alredyAnswerCombo = useSelector(
    (state) => state.shoppingCart.answersByOrderCombo[orderUuid],
  );

  React.useEffect(() => {
    dispatch(requireProducts(productId));
  }, [productId, dispatch]);

  // Quando a loja republica o cardápio (polling do autoatendimento), as
  // perguntas de todos os produtos voltam para INITIAL. Sem pedir de novo, a
  // tela aberta ficava em "Carregando..." até o cliente sair dela.
  React.useEffect(() => {
    if (stateStep === STATE_STEP.INITIAL) {
      dispatch(requireProducts(productId));
    }
  }, [stateStep, productId, dispatch]);

  React.useEffect(() => {
    const uuid = uuidv4();
    if (orderUuidEdit) {
      dispatch(startEditOrder({ uuid, productId, uuidEdit: orderUuidEdit }));
    } else {
      dispatch(
        startOrder({ uuid, productId, kilogramStep, upsell: fromUpsell }),
      );
    }
    setOrderUuid(uuid);
  }, [orderUuidEdit, productId, kilogramStep, fromUpsell, dispatch]);

  React.useEffect(() => {
    if (orderUuid && stateStep === STATE_STEP.READY && !alredyAnswerCombo) {
      dispatch(answerComboQuestion({ uuid: orderUuid, productId }));
    }
  }, [stateStep, orderUuid, productId, alredyAnswerCombo, dispatch]);

  const onBack = () => {
    dispatch(cancelOrder({ uuid: orderUuid }));
  };

  return {
    orderUuid,
    stateStep,
    alredyAnswerCombo,
    isOrderVisible: isAvailableToOrder && stateStep === STATE_STEP.READY,
    returnPath,
    onBack,
  };
}
